import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useStaticQuery, graphql, withAssetPrefix, withPrefix } from 'gatsby';

function Seo({
               description, lang, meta, titlePage, imagePage, slug, noindex, isBlogPost, datePublished
             }) {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          metaTitle
          description
          siteUrl
          favicon
          image
          social {
            twitter {
              name
            }
          }
        }
      }
    }
  `);

  const { social, siteUrl, metaTitle } = site.siteMetadata;

  const metaDescription = description || site.siteMetadata.description;
  const image = imagePage || withAssetPrefix(site.siteMetadata.image);
  const title = titlePage || metaTitle;
  const url = slug ? `${siteUrl}${withPrefix(slug)}` : siteUrl + __PATH_PREFIX__ + "/";
  const fbId = '1098993553882288';

  const organizationId = 'https://fibery.io/#organization';
  const blogPostId = `${url}#blogposting`;

  const structuredData = isBlogPost && datePublished
    ? {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id": blogPostId,
          "headline": title,
          "image": `${siteUrl}${image}`,
          "url": url,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": url
          },
          "datePublished": datePublished,
          "author": {
            "@type": "Organization",
            "name": "Fibery",
            "@id": organizationId
          },
          "publisher": {
            "@id": organizationId
          },
          "description": metaDescription
        }
      ]
    }
    : {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": organizationId,
          "name": "Fibery",
          "url": "https://fibery.io/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://fibery.io/favicon.svg"
          }
        },
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": metaTitle,
          "publisher": {
            "@id": organizationId
          }
        }
      ]
    };

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={`%s`}
      meta={[
        { name: `viewport`, content: 'width=device-width,initial-scale=1,shrink-to-fit=no,viewport-fit=cover' },
        { property: `og:title`, content: title },
        { property: `og:url`, content: url },
        { name: `twitter:title`, content: title },
        { name: `twitter:text:title`, content: title },
        { name: `description`, content: metaDescription },
        { property: `og:description`, content: metaDescription },
        { name: `twitter:description`, content: metaDescription },
        { property: `fb:app_id`, content: fbId },
        { property: `og:image`, content: `${siteUrl}${image}` },
        { property: `og:image:secure_url`, content: `${siteUrl}${image}` },
        { property: `og:image:width`, content: `1200` },
        { property: `og:image:height`, content: `628` },
        { name: `twitter:url`, content: url },
        { name: `twitter:image`, content: `${siteUrl}${image}` },
        { property: `og:image:alt`, content: `Fibery blog cover image` },
        { property: `og:type`, content: `website` },
        { property: `og:locale`, content: `en_US` },
        { property: `og:site_name`, content: metaTitle },
        { name: `twitter:card`, content: `summary_large_image` },
        { name: `twitter:site`, content: social.twitter.name },
        { name: `twitter:creator`, content: social.twitter.name },
        noindex && { name: "robots", content: "noindex" },
      ].concat(meta).filter(Boolean)}
    >
      <link rel="canonical" href={url} />
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

Seo.defaultProps = {
  titlePage: '',
  lang: 'en',
  meta: [],
  description: 'Visit Fibery blog to learn more about future of work, augmenting organizations intelligence, and no-code solutions.',
  isBlogPost: false,
  datePublished: null,
};

Seo.propTypes = {
  titlePage: PropTypes.string,
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  imagePage: PropTypes.string,
  slug: PropTypes.string,
  noindex: PropTypes.bool,
  isBlogPost: PropTypes.bool,
  datePublished: PropTypes.string,
};

export default Seo;
